import { clsx, type ClassValue } from "clsx";
import type { CSSProperties } from "react";
import { twMerge } from "tailwind-merge";

export type CssVariableStyle = CSSProperties &
  Record<`--${string}`, string | number>;

/**
 * Type a runtime-only CSS custom-property map without weakening React's
 * regular style-property checks. Static declarations still belong in classes.
 */
export function cssVars(
  values: Record<`--${string}`, string | number>,
): CssVariableStyle {
  return values as CssVariableStyle;
}

/** RFC 4122 UUID v4-style validation (8-4-4-4-12 hex). */
export function isValidUUID(value: string | null | undefined): value is string {
  return Boolean(
    value &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        value,
      ),
  );
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrencyWithCommas(
  value: number,
  decimals = 2,
  currencySymbol = "$",
): string {
  if (!Number.isFinite(value)) {
    return `${currencySymbol}0`;
  }
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${currencySymbol}${formatter.format(value)}`;
}

export function formatNumberWithCommas(value: number, decimals = 0): string {
  if (!Number.isFinite(value)) {
    return "0";
  }
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}
